/**
 * Places in the drive pickers (#651) — the Drive menu, inside every picker.
 *
 * People don't think in folders: they made a project, got an app, were shared
 * something. The Drive menu already answers "where is it" that way, so the
 * picker offers the SAME places, with the SAME contents as their Drive pages:
 *
 *   Library, My Space  → the folder (as Drive opens it)
 *   Projects, PDApps   → the Projects / PDApps page list — private and org
 *                        entities together, grouped the same way
 *   Shared             → the Shared page's incoming list
 *
 * Paths follow the person: a path someone clicks through STAYS where they
 * found it ("My Space › Projects › Q3 Report" stays under My Space). A path the
 * picker has to work out itself — where it opens, a search hit, a shortcut — is
 * told from where the person is if it lies there, and otherwise from the place
 * they would find it under ("Projects › Q3 Report", "My Space › Reports"), never
 * the storage layout ("papan › Persons › <email> › …") — and only under a list
 * place whose list shows it, so the parent column holds the folder they came from.
 *
 * A place that is a LIST is a location without a folder: pickers are told so
 * (`onNavigate(null, …)`) and have nothing to save into until an entry opens.
 */
import { useCallback, useMemo, type ReactNode } from 'react'
import { LayoutGrid, SquareLibrary, UserRound, Users } from 'lucide-react'
import { getActiveOrgEmail, getActiveTenantId } from '@papan/api-client'
import { useDriveNavTargets } from '@papan/files/client/queries'
import { FileIcon, PDAppIcon, type GlyphIcon } from '@papan/core-ui/ui'
import { useTranslation } from '@papan/i18n'
import { ROOT, isMiniappFolder, isProjectFolder, isSharedPointer, isSystemWorkspaceFolder, stripPointerSuffix } from '@papan/types'
import { classifyProjectSection, incomingSharedProjects, useProjects, useSharedOverview, type ProjectItem, type ProjectSection } from './home/data'
import { readableName } from './locations'
import { useDriveRootLabel } from './useDriveRootLabel'
import { getRootFolderId } from './picker-utils'
import { PickerRowButton } from './PickerRowButton'

export type PlaceKey = 'library' | 'mySpace' | 'projects' | 'pdapps' | 'shared'

export interface PickerPlace {
  key: PlaceKey
  label: string
  icon: GlyphIcon
  /** The folder a folder place opens; null for a place that is a list. */
  folderId: string | null
  /** Not openable yet: which folder it opens is still loading. */
  pending?: boolean
}

interface Crumb {
  id: string
  name: string
  /** Set on a crumb `rebase` made to stand for a folder place (My Space, Library). */
  place?: PlaceKey
}

const PLACE_CRUMB_PREFIX = 'place:'
const LIST_PLACES: ReadonlySet<PlaceKey> = new Set(['projects', 'pdapps', 'shared'])

/** The crumb that stands for a list place in a picker's path. Never a real folder id. */
export function placeCrumb(place: PickerPlace): Crumb {
  return { id: `${PLACE_CRUMB_PREFIX}${place.key}`, name: place.label }
}

/** The list place a crumb stands for, or null for a real folder. */
export function listPlaceOf(crumbId: string | undefined): PlaceKey | null {
  if (!crumbId?.startsWith(PLACE_CRUMB_PREFIX)) return null
  const key = crumbId.slice(PLACE_CRUMB_PREFIX.length) as PlaceKey
  return LIST_PLACES.has(key) ? key : null
}

/** Whether the Projects / PDApps lists show a project or app (they leave out what only admin view reveals). */
function listsEntity(item: ProjectItem): boolean {
  return !item.hiddenWithoutAdminView
}

// A system `.workspace` (assistant chat surface) is named like a project but is in no list.
const isEntityName = (name: string) => (isProjectFolder(name) && !isSystemWorkspaceFolder(name)) || isMiniappFolder(name)

/**
 * Which lists the given paths could be filed under — so a picker reads a list
 * (an expensive one) only once a path needs it for its place.
 */
export function listsNeededBy(paths: Iterable<readonly Crumb[] | null | undefined>): { entities: boolean; shares: boolean } {
  let entities = false
  let shares = false
  for (const path of paths) {
    // Inside a share the share decides the place: names within it need no list.
    // (Judged by name, before the Shared list says whether it IS a share: a
    // plain folder merely named `X.shared` holding an admin-only project can
    // label a search hit under Projects until that hit is opened, which reads
    // the list and corrects it. Accepted: knowing sooner would need the Shared
    // list's answer here, which is itself read because of this.)
    let inShare = false
    for (const crumb of path ?? []) {
      const listed = listPlaceOf(crumb.id)
      if (listed === 'shared' || isSharedPointer(crumb.name)) {
        shares = true
        inShare = true
      } else if (!inShare && (listed || isEntityName(crumb.name))) {
        entities = true
      }
    }
  }
  return { entities, shares }
}

/**
 * The list place a path is found under, and where in the path its entry starts.
 *
 * A SHARE decides first, the outermost one: what lies inside it belongs to the
 * source drive and is in none of this drive's lists. A share of a project or
 * app is listed with them (as the Projects page lists it); any other under
 * Shared. Otherwise the INNERMOST project or app decides — the lists hold
 * nested ones too, and it is the one you are in. Either way, only one its
 * list shows (a folder merely NAMED like a share or a project is not one).
 */
function listPlaceFor(stack: readonly Crumb[], isListed: (crumb: Crumb) => boolean): { key: PlaceKey; at: number } | null {
  const shareAt = stack.findIndex(crumb => isSharedPointer(crumb.name) && isListed(crumb))
  if (shareAt >= 0) {
    const target = stripPointerSuffix(stack[shareAt].name)
    const key: PlaceKey = isMiniappFolder(target) ? 'pdapps' : isProjectFolder(target) ? 'projects' : 'shared'
    return { key, at: shareAt }
  }
  for (let at = stack.length - 1; at >= 0; at--) {
    const { name } = stack[at]
    if (!isSharedPointer(name) && isEntityName(name) && isListed(stack[at])) {
      return { key: isMiniappFolder(name) ? 'pdapps' : 'projects', at }
    }
  }
  return null
}

export interface PickerPlaces {
  /** Empty when the picker doesn't browse the active tenant's whole drive. */
  places: PickerPlace[]
  /**
   * A root-anchored path the picker worked out (from breadcrumbs), told as the
   * person should see it: under `from` — the path they are on — if it lies
   * inside it, else from the place they would find it under. Only the crumbs it
   * adds are display names; every crumb it keeps keeps its real folder name
   * (callers build paths from them), so render names through `readableName`.
   */
  rebase: (stack: Crumb[], from?: Crumb[]) => Crumb[]
  /**
   * Whether a path filed under a list place is one that list shows. False only
   * once the list has loaded without it — a path filed before then is re-told.
   */
  placeHolds: (stack: Crumb[]) => boolean
  /** Which place a path is under, if any — for a one-crumb path, whether it IS a place. */
  activeKey: (stack: Crumb[]) => PlaceKey | null
  /** The drive root, named as Drive names it — where Up leads from a place. Null without places. */
  driveRoot: Crumb | null
  /**
   * A stored path as shown now: the drive root named as Drive names it (the
   * name can load after the path was stored), and the viewer's own
   * `Persons/<email>` read as My Space in a path clicked through from the root.
   */
  labelled: (stack: Crumb[]) => Crumb[]
}

/**
 * The places, resolved by the same hook as the Drive menu (`useDriveNavTargets`)
 * so the two can't disagree. Only for a picker whose root IS the active tenant's
 * drive root — one scoped to a folder (an app's own folder, a project) must not
 * offer places outside its roots.
 *
 * `needs` says which lists the picker's paths could be filed under
 * (`listsNeededBy`); a list is read only then.
 */
export function usePickerPlaces(
  roots: ReadonlyArray<{ id: string }>,
  needs: { entities: boolean; shares: boolean },
): PickerPlaces {
  const { t } = useTranslation('files')
  const tenantId = getActiveTenantId()
  const rootId = tenantId ? getRootFolderId(tenantId) : null
  const browsesDrive = !!rootId && roots.some(root => root.id === rootId)
  const isOrg = !!tenantId && !tenantId.startsWith('p_')
  const viewerEmail = isOrg ? getActiveOrgEmail()?.toLowerCase() ?? null : null
  const { rootListed, libraryId, mySpaceId } = useDriveNavTargets({ isOrg, orgEmail: viewerEmail, enabled: browsesDrive })
  const rootLabel = useDriveRootLabel()

  // What the Projects / PDApps and Shared lists show, so a folder is filed
  // under one only if it shows it. Until a list loads, everything named like
  // its entries counts as shown; a list that fails to load shows nothing.
  // Kept current by `FileListCacheSync`, the one session-wide listener (#1058).
  const projects = useProjects(browsesDrive && needs.entities)
  const overview = useSharedOverview(false, { refetchOnMount: true, enabled: browsesDrive && needs.shares })
  const listedEntityIds = useMemo(
    () => (projects.data ? new Set(projects.data.filter(listsEntity).map(item => item.folderId)) : projects.isError ? new Set<string>() : null),
    [projects.data, projects.isError],
  )
  const listedShareIds = useMemo(
    () => (overview.data ? new Set(overview.data.incoming.filter(share => !share.broken).map(share => share.id)) : overview.isError ? new Set<string>() : null),
    [overview.data, overview.isError],
  )
  const isListed = useCallback((crumb: Crumb) => {
    const ids = isSharedPointer(crumb.name) ? listedShareIds : listedEntityIds
    return !ids || ids.has(crumb.id)
  }, [listedEntityIds, listedShareIds])

  const places = useMemo<PickerPlace[]>(() => {
    if (!browsesDrive || !rootId) return []
    const all: Array<PickerPlace | null> = [
      // A tenant from before the Library folder existed: Drive's Library opens
      // the root — known only once the root listing answers.
      { key: 'library', label: t('driveHome.nav.library'), icon: SquareLibrary, folderId: libraryId ?? rootId, pending: !libraryId && !rootListed },
      mySpaceId ? { key: 'mySpace', label: t('driveHome.nav.mySpace'), icon: UserRound, folderId: mySpaceId } : null,
      { key: 'projects', label: t('driveHome.nav.projects'), icon: LayoutGrid, folderId: null },
      { key: 'pdapps', label: t('driveHome.nav.miniapps'), icon: PDAppIcon, folderId: null },
      { key: 'shared', label: t('driveHome.nav.shared'), icon: Users, folderId: null },
    ]
    return all.filter((place): place is PickerPlace => place !== null)
  }, [browsesDrive, rootId, rootListed, libraryId, mySpaceId, t])

  const placeKeyOf = useCallback(
    // A folder place counts only as a path's own start; the drive root itself is
    // no place, even where Library falls back to it.
    (crumb: Crumb): PlaceKey | null => listPlaceOf(crumb.id) ?? crumb.place ?? places.find(p => p.folderId === crumb.id && p.folderId !== rootId)?.key ?? null,
    [places, rootId],
  )

  const rebase = useCallback((stack: Crumb[], from?: Crumb[]): Crumb[] => {
    if (places.length === 0 || stack.length === 0) return stack

    // Inside the path the person is on (a search hit, a shortcut): it stays
    // under that path — the deepest folder of it the target lies in. Not the
    // bare drive root (everything lies there) nor a list place (not a folder).
    for (let i = (from?.length ?? 0) - 1; i >= 0; i--) {
      const crumb = from![i]
      if (crumb.id === rootId || listPlaceOf(crumb.id)) continue
      const at = stack.findIndex(c => c.id === crumb.id)
      if (at >= 0) return [...from!.slice(0, i + 1), ...stack.slice(at + 1)]
    }

    // Made as a project or app, or shared with this drive → found under
    // Projects / PDApps / Shared, wherever it is stored.
    const listed = listPlaceFor(stack, isListed)
    const place = listed && places.find(p => p.key === listed.key)
    if (listed && place) return [placeCrumb(place), ...stack.slice(listed.at)]

    // Anything else is told from the root; a path already told from a place stays.
    if (stack[0].id !== rootId) return stack
    const below = stack.slice(1)
    // The viewer's own `Persons/<email>` (directly under the root) → My Space.
    // Matched by path, not by the resolved place id, so a path built before
    // that id loads is told the same way.
    if (below[0]?.name === ROOT.PERSONS && below[1] && below[1].name.toLowerCase() === viewerEmail) {
      return [{ id: below[1].id, name: t('driveHome.nav.mySpace'), place: 'mySpace' }, ...below.slice(2)]
    }
    if (below[0]?.name === ROOT.LIBRARY) return [{ id: below[0].id, name: t('driveHome.nav.library'), place: 'library' }, ...below.slice(1)]
    return [{ ...stack[0], name: rootLabel }, ...below]
  }, [places, isListed, rootId, rootLabel, viewerEmail, t])

  const placeHolds = useCallback((stack: Crumb[]): boolean => {
    const entry = stack[1]
    return !listPlaceOf(stack[0]?.id) || !entry || isListed(entry)
  }, [isListed])

  const activeKey = useCallback((stack: Crumb[]): PlaceKey | null => (stack[0] ? placeKeyOf(stack[0]) : null), [placeKeyOf])

  const driveRoot = useMemo(
    () => (places.length > 0 && rootId ? { id: rootId, name: rootLabel } : null),
    [places.length, rootId, rootLabel],
  )

  const labelled = useCallback(
    (stack: Crumb[]) => {
      if (!driveRoot || stack[0]?.id !== driveRoot.id) return stack
      return stack.map((crumb, i) => {
        if (i === 0) return { ...crumb, name: driveRoot.name }
        // The viewer's own space is `Persons/<email>` directly under the root, as `rebase` matches it.
        const ownSpace = i === 2 && !!viewerEmail && crumb.name.toLowerCase() === viewerEmail && stack[1].name === ROOT.PERSONS
        return ownSpace ? { ...crumb, name: t('driveHome.nav.mySpace') } : crumb
      })
    },
    [driveRoot, viewerEmail, t],
  )

  return { places, rebase, placeHolds, activeKey, driveRoot, labelled }
}

interface PlacesNavProps {
  places: PickerPlace[]
  activeKey: PlaceKey | null
  onChoose: (place: PickerPlace) => void
}

/**
 * Places as a column beside the listing (wide screens) or a scrollable row
 * above it (narrow).
 *
 * The column runs the picker's full height, beside the folder panel and its
 * toolbar. It is EXPANDED (icon + label) while the picker is at a place itself
 * — there is no parent folder to show beside it then — and shrinks to icons
 * once a subfolder is open, making room for the parent column. Hovering or
 * focusing the icons slides the labels out OVER the columns, so nothing shifts.
 */
export function PlacesNav({ places, activeKey, onChoose, layout, expanded = true, search }: PlacesNavProps & {
  layout: 'column' | 'row'
  expanded?: boolean
  /**
   * The column's top row — the picker's search box, given the class that hides
   * what a collapsed column only shows on hover or focus. Without it the row
   * is the "Drive" heading.
   */
  search?: (collapsedHidden: string, fieldTone: string) => ReactNode
}) {
  const { t } = useTranslation('files')
  if (layout === 'row') {
    return (
      <nav aria-label={t('driveHome.nav.title')} data-testid="picker-places-row" className="sm:hidden flex gap-1.5 overflow-x-auto px-2 py-1.5 border-b themed-border">
        {places.map(place => (
          <button
            key={place.key}
            type="button"
            onClick={() => onChoose(place)}
            disabled={place.pending}
            aria-current={activeKey === place.key ? 'location' : undefined}
            data-testid={`picker-place-row-${place.key}`}
            className={`shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-full border themed-border text-xs transition-colors disabled:opacity-50 ${activeKey === place.key ? 'themed-bg-tertiary themed-text' : 'themed-text-secondary hover:themed-text'}`}
          >
            <place.icon size={12} className="shrink-0" />
            {place.label}
          </button>
        ))}
      </nav>
    )
  }
  // Collapsed: labels are still in the DOM (screen readers, tooltips-free) but hidden until hover/focus.
  // Hover expands after a beat: the open rail covers the folder panel's Back
  // button, and a pointer only passing over the icons on its way there must
  // not open it. Keyboard focus and collapsing stay immediate.
  const label = expanded ? '' : 'opacity-0 group-hover/places:opacity-100 group-hover/places:delay-200 group-focus-within/places:opacity-100 group-focus-within/places:delay-0 transition-opacity'
  const width = expanded
    ? 'w-40'
    : 'w-11 themed-bg-secondary hover:w-40 hover:delay-200 focus-within:w-40 focus-within:delay-0 hover:shadow-xl focus-within:shadow-xl'
  return (
    <div data-testid="picker-places-column" className={`relative hidden sm:block shrink-0 transition-[width] duration-150 ${expanded ? 'w-40' : 'w-11'}`}>
      {/* ONE panel: the search row and the places open together, on hover or
          focus anywhere in the column. While open it lies over the folder
          panel's leading edge — its Back / Forward / Up included. Leaving the
          column closes it at once; a focused search field holds it open until
          focus leaves (click elsewhere, Tab, or Escape). */}
      <div
        data-testid="picker-places-panel"
        className={`group/places absolute inset-y-0 left-0 z-20 flex flex-col border-r themed-border transition-[width,box-shadow] duration-150 ${width}`}
      >
        {/* As tall as the folder panel's toolbar (h-10), with a small gap below it. */}
        {search && (
          <div className={`h-10 shrink-0 mb-1 flex items-center px-1.5 ${expanded ? '' : 'overflow-hidden group-hover/places:overflow-visible group-focus-within/places:overflow-visible'}`}>
            {/* Collapsed, the magnifier sits bare like the place icons; the field
                takes its tint as the column opens (rules in index.css). */}
            {search(label, expanded
              ? 'themed-bg-tertiary'
              : 'transition-colors group-hover/places:themed-bg-tertiary group-hover/places:delay-200 group-focus-within/places:themed-bg-tertiary group-focus-within/places:delay-0')}
          </div>
        )}
        <nav
          aria-label={t('driveHome.nav.title')}
          data-testid="picker-places"
          data-expanded={expanded || undefined}
          className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden pb-1.5 px-1.5"
        >
          {!search && <div className={`h-10 flex items-center px-2 text-[10px] uppercase tracking-wide themed-text-muted whitespace-nowrap ${label}`}>{t('driveHome.nav.title')}</div>}
          {places.map(place => {
            const active = activeKey === place.key
            return (
              <button
                key={place.key}
                type="button"
                onClick={() => onChoose(place)}
                disabled={place.pending}
                aria-current={active ? 'location' : undefined}
                data-testid={`picker-place-${place.key}`}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-[13px] text-left whitespace-nowrap transition-colors disabled:opacity-50 ${active ? 'themed-bg-tertiary themed-text' : 'themed-text-secondary hover:themed-text hover:themed-bg-tertiary'}`}
              >
                <place.icon size={14} className="shrink-0" />
                <span className={`truncate ${label}`}>{place.label}</span>
              </button>
            )
          })}
        </nav>
      </div>
    </div>
  )
}

/** A place list that could not be read — said so, never shown as empty. */
function ListLoadFailed({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('files')
  return (
    <div className="px-3 py-3 text-sm themed-text-muted">
      {t('drivePicker.loadFailed')}{' '}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onRetry() }}
        className="text-indigo-400 hover:text-indigo-300 cursor-pointer"
      >
        {t('drivePicker.retry')}
      </button>
    </div>
  )
}

const SECTION_ORDER: ProjectSection[] = ['mySpace', 'myTeams', 'org', 'outsider']
const SECTION_LABEL_KEY: Record<ProjectSection, string> = {
  mySpace: 'driveHome.projectsPage.sectionMySpace',
  myTeams: 'driveHome.projectsPage.sectionMyTeams',
  org: 'driveHome.projectsPage.sectionOrg',
  outsider: 'driveHome.projectsPage.sectionOutsider',
}

export interface PlaceEntry {
  folderId: string
  name: string
}

function EntryRow({ icon, name, detail, folderId, currentFolderId, selectedId, onOpen }: {
  icon: ReactNode
  name: string
  detail?: string | null
  folderId: string
  onOpen: () => void
} & Pick<PlaceListProps, 'currentFolderId' | 'selectedId'>) {
  return (
    <PickerRowButton
      selected={!!selectedId && folderId === selectedId}
      current={!!currentFolderId && folderId === currentFolderId}
      onClick={onOpen}
      testId="picker-place-entry"
    >
      {icon}
      <span className="flex-1 min-w-0 themed-text truncate">{name}</span>
      {detail && <span className="text-[11px] themed-text-muted truncate max-w-[40%]">{detail}</span>}
    </PickerRowButton>
  )
}

interface PlaceListProps {
  /** The entry whose folder is open (the parent column inside a project). */
  currentFolderId?: string | null
  /** The picker's selection, when it is one of the entries. */
  selectedId?: string | null
  /** Entries the picker must not offer (its `hideItem`: e.g. the folder being moved). */
  hideEntry?: (entry: PlaceEntry) => boolean
  onOpen: (entry: PlaceEntry) => void
}

/**
 * The contents of a list place — the Projects / PDApps page list (the same
 * queries and sections, including work another organisation shared in) or the
 * Shared page's incoming list. Opening an entry opens its folder. Unscrolled:
 * the caller's pane scrolls it.
 */
export function PlaceListView({ place, ...props }: PlaceListProps & { place: PlaceKey }) {
  if (place === 'shared') return <SharedList {...props} />
  return <EntityList kind={place === 'pdapps' ? 'pdapps' : 'projects'} {...props} />
}

function EntityList({ kind, hideEntry, onOpen, ...rows }: PlaceListProps & { kind: 'projects' | 'pdapps' }) {
  const { t } = useTranslation('files')
  // The Projects page's two queries: the drive's own work, and work shared in
  // from another organisation (a `.shared` pointer, classified by its target).
  const projects = useProjects()
  const overview = useSharedOverview(false, { refetchOnMount: true })
  const incoming = useMemo(() => incomingSharedProjects(overview.data?.incoming ?? []), [overview.data])
  const tenantId = getActiveTenantId()
  const isOrg = !!tenantId && !tenantId.startsWith('p_')
  const orgEmail = getActiveOrgEmail()

  const sections = useMemo(() => {
    const ofKind = [...(projects.data ?? []), ...incoming.filter(p => !p.broken)]
      .filter(p => listsEntity(p) && (kind === 'pdapps') === isMiniappFolder(stripPointerSuffix(p.name)))
    const entities = ofKind
      .filter(p => !hideEntry?.({ folderId: p.folderId, name: p.name }))
      .sort((a, b) => readableName(a.name).localeCompare(readableName(b.name)))
    const grouped = isOrg
      ? SECTION_ORDER
        .map(section => ({ section, entities: entities.filter(p => classifyProjectSection(p, orgEmail) === section) }))
        .filter(group => group.entities.length > 0)
      : [{ section: null, entities }]
    return { grouped, hiddenOnly: ofKind.length > 0 && entities.length === 0 }
  }, [projects.data, incoming, kind, hideEntry, isOrg, orgEmail])

  // Work shared in arrives on its own (slower) read and joins the list when it does.
  if (projects.isLoading) return <div className="px-3 py-3 text-sm themed-text-muted">{t('driveHome.loading')}</div>
  if (projects.isError) return <ListLoadFailed onRetry={() => void projects.refetch()} />
  // Work shared in that could not be read is said so, never silently missing.
  const sharedInFailed = overview.isError && <ListLoadFailed onRetry={() => void overview.refetch()} />
  if (sections.grouped.every(group => group.entities.length === 0)) {
    if (sharedInFailed) return sharedInFailed
    // "No projects yet" only when there are none — not when the picker hid the only one.
    const empty = sections.hiddenOnly ? 'newProject.pickerEmpty' : kind === 'pdapps' ? 'newProject.parentEmptyMiniapp' : 'newProject.parentEmptyProject'
    return <div className="px-3 py-3 text-sm themed-text-muted">{t(empty)}</div>
  }
  return (
    <div data-testid={`picker-place-list-${kind}`}>
      {sections.grouped.map(({ section, entities }) => (
        <div key={section ?? 'all'} className="mb-2">
          {section && <div className="px-2 pt-1 pb-0.5 text-[10px] uppercase tracking-wide themed-text-muted">{t(SECTION_LABEL_KEY[section])}</div>}
          {entities.map(entity => (
            <EntryRow
              key={entity.folderId}
              icon={kind === 'pdapps'
                ? <PDAppIcon size={15} className="text-pink-400 shrink-0" />
                : <LayoutGrid size={15} className="text-purple-400 shrink-0" />}
              name={readableName(entity.name)}
              folderId={entity.folderId}
              {...rows}
              onOpen={() => onOpen({ folderId: entity.folderId, name: entity.name })}
            />
          ))}
        </div>
      ))}
      {sharedInFailed}
    </div>
  )
}

function SharedList({ hideEntry, onOpen, ...rows }: PlaceListProps) {
  const { t } = useTranslation('files')
  // Not refetched on every mount: moving between this list and a share inside
  // it remounts the list, and the overview is an expensive read.
  const overview = useSharedOverview(false, { refetchOnMount: true })
  // Only what can be opened as a folder: a picker browses into it.
  const incoming = overview.data?.incoming
  const shares = useMemo(
    () => (incoming ?? [])
      .filter(share => !share.broken && share.isFolder !== false)
      .filter(share => !hideEntry?.({ folderId: share.id, name: share.name }))
      .sort((a, b) => a.name.localeCompare(b.name)),
    [incoming, hideEntry],
  )
  if (overview.isLoading) return <div className="px-3 py-3 text-sm themed-text-muted">{t('driveHome.loading')}</div>
  if (overview.isError) return <ListLoadFailed onRetry={() => void overview.refetch()} />
  if (shares.length === 0) {
    // "Nothing has been shared" only when nothing has — not when what arrived can't be browsed into.
    const empty = incoming && incoming.length > 0 ? 'newProject.pickerEmpty' : 'driveHome.sharedPage.emptyIncoming'
    return <div className="px-3 py-3 text-sm themed-text-muted">{t(empty)}</div>
  }
  return (
    <div data-testid="picker-place-list-shared">
      {shares.map(share => (
        <EntryRow
          key={share.id}
          icon={<FileIcon name={share.name} isFolder size={15} />}
          name={readableName(share.name)}
          detail={share.sourceTenantName}
          folderId={share.id}
          {...rows}
          onOpen={() => onOpen({ folderId: share.id, name: share.name })}
        />
      ))}
    </div>
  )
}
